import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as CONSTANTS from '../shared/constants';
import usePlayerMovement from '../hooks/usePlayerMovement';
import { 
  BombermanContextProvider,
  useGameState,
  usePlayer,
  useGameObjects,
  useConnection,
  useAudio,
  useGraphics,
  useAnimation
} from '../contexts';
import GameLobby from './GameLobby';
import PlayerStatsDisplay from './PlayerStatsDisplay';
import TopBar from './TopBar';
import ConnectionStatus from './ConnectionStatus';
import GameCanvas from './GameCanvas';
import { createGameRenderer } from './renderers/GameRenderer';

// Import constants from shared file
const {
  CANVAS_WIDTH,
  CANVAS_HEIGHT,
  GRID_SIZE
} = CONSTANTS;

// Main game component that uses contexts
const BombermanGame = ({ roomId }) => {
  const canvasRef = useRef(null);
  
  // Get all context values
  const gameState = useGameState();
  const player = usePlayer();
  const gameObjects = useGameObjects();
  const connection = useConnection();
  const audio = useAudio();
  const graphics = useGraphics();
  const animation = useAnimation();

  // Try to restore player ID on component mount
  useEffect(() => {
    if (!player.myPlayerIdRef.current) {
      const storedPlayerId = player.getStoredPlayerId();
      if (storedPlayerId) {
        player.myPlayerIdRef.current = storedPlayerId;
        player.setMyPlayerId(storedPlayerId);
      }
    }
    if (!player.myUniquePlayerIdRef.current) {
      const storedUniquePlayerId = player.getStoredUniquePlayerId();
      if (storedUniquePlayerId) {
        player.myUniquePlayerIdRef.current = storedUniquePlayerId;
      }
    }
  }, [player]);

  // Canvas scaling state
  const [canvasScale] = useState(1);

  // Use player movement hook with context values
  const {
    interpolatePosition,
    handleKeyDown,
    handleKeyUp
  } = usePlayerMovement({
    myPlayer: player.myPlayer,
    myPlayerId: player.myPlayerId,
    gameState: gameState.gameState,
    isObserver: player.isObserver,
    bombs: gameObjects.bombs,
    walls: gameObjects.walls,
    setPlayers: player.setPlayers,
    emitPlayerInput: connection.emitPlayerInput,
    emitKickBomb: connection.emitKickBomb,
    playerAnimationState: animation.playerAnimationState
  });

  // Game actions using contexts
  const placeBomb = useCallback(() => {
    if (!player.myPlayer || !player.myPlayer.alive || gameState.gameState !== 'playing' || player.isObserver) return;

    const gridX = Math.floor((player.myPlayer.x + GRID_SIZE/2) / GRID_SIZE);
    const gridY = Math.floor((player.myPlayer.y + GRID_SIZE/2) / GRID_SIZE);
    
    const existingBomb = gameObjects.findBombAt(gridX, gridY);
    if (existingBomb) return;
    
    connection.emitPlaceBomb({ x: gridX, y: gridY });
  }, [player.myPlayer, gameState.gameState, gameObjects, player.isObserver, connection]);

  const toggleReady = useCallback(() => {
    if (gameState.gameState !== 'lobby' || player.isObserver) return;
    connection.emitToggleReady();
  }, [gameState.gameState, player.isObserver, connection]);

  const joinAsPlayer = useCallback(() => {
    if (gameState.gameState !== 'lobby' || !player.isObserver) return;
    connection.emitJoinAsPlayer();
  }, [gameState.gameState, player.isObserver, connection]);

  const becomeObserver = useCallback(() => {
    if (gameState.gameState !== 'lobby' || player.isObserver) return;
    connection.emitBecomeObserver();
  }, [gameState.gameState, player.isObserver, connection]);

  // Create the game renderer ref
  const drawRef = useRef();

  // Ref to store current state for draw function without dependencies
  const drawStateRef = useRef();
  drawStateRef.current = {
    canvasRef,
    players: player.players,
    walls: gameObjects.walls,
    bombs: gameObjects.bombs,
    explosions: gameObjects.explosions,
    upgrades: gameObjects.upgrades,
    gameState: gameState.gameState,
    winner: gameState.winner,
    gameOverCountdown: gameState.gameOverCountdown,
    bombImage: graphics.bombImage,
    bombSVGText: graphics.bombSVGText,
    destructibleWallImage: graphics.destructibleWallImage,
    indestructibleWallImage: graphics.indestructibleWallImage,
    floorImage: graphics.floorImage,
    fireCenterImage: graphics.fireCenterImage,
    fireLineImage: graphics.fireLineImage,
    fireEndImage: graphics.fireEndImage,
    myPlayerId: player.myPlayerId,
    characterSprites: graphics.characterSprites,
    characterSpriteSVGs: graphics.characterSpriteSVGs,
    createColoredSprite: graphics.createColoredSprite,
    coloredSpritesCache: graphics.coloredSpritesCache,
    coloredBombCache: graphics.coloredBombCache,
    createColoredBomb: graphics.createColoredBomb,
    bombStateBuffers: animation.bombStateBuffers,
    interpolatePosition,
    playerStateBuffers: animation.playerStateBuffers,
    playerAnimationState: animation.playerAnimationState,
    drawRef
  };

  // Initialize the game renderer
  drawRef.current = createGameRenderer(drawStateRef);

  // Continuous animation loop for bomb breathing and other animations
  useEffect(() => {
    let animationId;
    
    const animate = () => {
      drawRef.current();
      animationId = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, []); // No dependencies - draw function is stable via ref

  // Canvas resizing is now handled by the GameCanvas component itself

  // Handle keyboard input
  useEffect(() => {
    const handleKeyDownWrapper = (e) => {
      handleKeyDown(e);
      // Bomb placement is now handled server-side via input
      if (e.key === ' ') {
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyDownWrapper);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDownWrapper);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [handleKeyDown, handleKeyUp, placeBomb, gameState.gameState, player.myPlayer, player.isObserver]);

  const getCurrentRoomId = () => {
    return window.location.pathname.split('/').pop();
  };

  const copyRoomUrl = () => {
    const roomId = getCurrentRoomId();
    const url = `${window.location.origin}/game/${roomId}`;
    navigator.clipboard.writeText(url);
  };

  // Handle connection states
  if (gameState.isConnecting() || gameState.hasError()) {
    return (
      <ConnectionStatus 
        gameState={gameState.gameState}
        connectionError={gameState.connectionError}
        onRetry={() => window.location.reload()}
      />
    );
  }

  return (
    <div style={{ 
      backgroundColor: '#000', 
      minHeight: '100vh', 
      color: '#fff',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <TopBar
        gameState={gameState.gameState}
        players={player.players}
        observers={player.observers}
        myPlayerId={player.myPlayerId}
        isObserver={player.isObserver}
        volume={audio.volume}
        audioEnabled={audio.audioEnabled}
        onVolumeChange={audio.handleVolumeChange}
      />
      
      {/* Main Content Area */}
      <div style={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: gameState.isInLobby() ? 'center' : 'flex-start',
        padding: gameState.isInLobby() ? '20px' : '10px'
      }}>
        {gameState.isInLobby() && (
          <GameLobby
            players={player.players}
            observers={player.observers}
            myPlayerId={player.myPlayerId}
            myPlayer={player.myPlayer}
            isObserver={player.isObserver}
            audioEnabled={audio.audioEnabled}
            lobbyCountdown={gameState.lobbyCountdown}
            svgSpritesLoaded={graphics.svgSpritesLoaded}
            characterSpriteSVGs={graphics.characterSpriteSVGs}
            coloredSpritesCache={graphics.coloredSpritesCache}
            createColoredSprite={graphics.createColoredSprite}
            toggleReady={toggleReady}
            joinAsPlayer={joinAsPlayer}
            becomeObserver={becomeObserver}
            copyRoomUrl={copyRoomUrl}
            enableAudio={audio.enableAudio}
          />
        )}
      
        {(gameState.isPlaying() || gameState.isGameOver()) && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', width: '100%', flex: 1 }}>
            <PlayerStatsDisplay 
              players={player.players}
              gameState={gameState.gameState}
              gameTimer={gameState.gameTimer}
              suddenDeath={gameState.suddenDeath}
            />
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, width: '100%' }}>
              <GameCanvas 
                ref={canvasRef}
                gameState={gameState.gameState}
                canvasScale={canvasScale}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Main component wrapper with context provider
const BombermanGameLoop = ({ roomId, nickname }) => {
  return (
    <BombermanContextProvider roomId={roomId} nickname={nickname}>
      <BombermanGame roomId={roomId} />
    </BombermanContextProvider>
  );
};

export default BombermanGameLoop;